import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from "@/components/ui/button";
import { 
    ArrowRight, Check, X, Info, 
    Users, Sparkles, Factory, Wifi, Target, Coffee, MapPin, Monitor, Mail, DoorOpen, Clock, Search, Calendar as CalendarIcon, Ticket, Building2, ExternalLink
} from "lucide-react";
import {
  SwipeableDialog as Dialog,
  SwipeableDialogContent as DialogContent,
  SwipeableDialogHeader as DialogHeader,
  SwipeableDialogTitle as DialogTitle,
  SwipeableDialogTrigger as DialogTrigger,
} from "@/components/ui/swipeable-dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from 'axios';
import { EventList } from "@/components/EventList";

// Icon Map (unchanged)
const ICON_MAP = {
    'users': Users, 'sparkles': Sparkles, 'factory': Factory, 'wifi': Wifi, 'target': Target, 'coffee': Coffee, 'map-pin': MapPin, 'monitor': Monitor, 'mail': Mail, 'door-open': DoorOpen, 'clock': Clock, 'search': Search, 'check': Check
};

// Lightbox - Improved Mobile Experience with Lazy Loading & Preloading & Flow Animation
const Lightbox = ({ images, initialIndex = 0, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [loadedImages, setLoadedImages] = useState(new Set());
    const [slideDirection, setSlideDirection] = useState(null); // 'left', 'right', or null
    const [isAnimating, setIsAnimating] = useState(false);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const touchEndX = useRef(0);
    const touchEndY = useRef(0);
    const isDragging = useRef(false);

    // Preload current, next and previous images
    useEffect(() => {
        const preloadIndices = [
            currentIndex,
            (currentIndex + 1) % images.length,
            (currentIndex - 1 + images.length) % images.length
        ];
        
        preloadIndices.forEach(idx => {
            if (!loadedImages.has(idx)) {
                const img = new Image();
                img.onload = () => {
                    setLoadedImages(prev => new Set([...prev, idx]));
                };
                img.src = images[idx];
            }
        });
    }, [currentIndex, images, loadedImages]);

    // Compute loading state based on loadedImages
    const isCurrentLoading = !loadedImages.has(currentIndex);

    const showNext = React.useCallback((e) => {
        e?.stopPropagation();
        if (isAnimating) return;
        setSlideDirection('left');
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length);
            setSlideDirection(null);
            setIsAnimating(false);
        }, 250);
    }, [images.length, isAnimating]);

    const showPrev = React.useCallback((e) => {
        e?.stopPropagation();
        if (isAnimating) return;
        setSlideDirection('right');
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
            setSlideDirection(null);
            setIsAnimating(false);
        }, 250);
    }, [images.length, isAnimating]);

    const handleKeyDown = React.useCallback((e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onClose();
        }
        if (e.key === 'ArrowRight') showNext(e);
        if (e.key === 'ArrowLeft') showPrev(e);
    }, [onClose, showNext, showPrev]);

    const handleTouchStart = (e) => {
        touchStartX.current = e.changedTouches[0].screenX;
        touchStartY.current = e.changedTouches[0].screenY;
        isDragging.current = true;
    };

    const handleTouchMove = (e) => {
        if (!isDragging.current) return;
        touchEndX.current = e.changedTouches[0].screenX;
        touchEndY.current = e.changedTouches[0].screenY;
    };

    const handleTouchEnd = (e) => {
        if (!isDragging.current) return;
        isDragging.current = false;
        
        const deltaX = touchStartX.current - touchEndX.current;
        const deltaY = touchStartY.current - touchEndY.current;
        const threshold = 50;
        
        // Only trigger swipe if horizontal movement is greater than vertical
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX > threshold) showNext();
            else if (deltaX < -threshold) showPrev();
        } else if (Math.abs(deltaY) > threshold * 2 && deltaY < 0) {
            // Swipe down to close
            onClose();
        }
    };

    React.useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, true);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            document.body.style.overflow = 'unset';
        };
    }, [handleKeyDown]);

    if (!images || images.length === 0) return null;

    return createPortal(
        <div 
            className="fixed inset-0 z-[10000] bg-black/95 flex items-center justify-center animate-in fade-in duration-300 backdrop-blur-sm" 
            style={{ pointerEvents: 'auto' }}
            onClick={onClose}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Close button - larger touch target on mobile */}
            <button 
                className="absolute top-4 right-4 text-white/90 hover:text-white transition-colors p-4 z-[10001] cursor-pointer active:scale-90 bg-black/30 rounded-full md:bg-transparent md:rounded-none" 
                onClick={(e) => { e.stopPropagation(); onClose(); }} 
                aria-label="Galerie schließen"
            >
                <X size={32} className="md:w-9 md:h-9" aria-hidden="true" />
            </button>
            
            {/* Navigation hint on mobile */}
            {images.length > 1 && (
                <div className="md:hidden absolute top-4 left-4 text-white/60 text-xs bg-black/30 px-3 py-2 rounded-full">
                    ← Wischen zum Navigieren →
                </div>
            )}
            
            {images.length > 1 && (
                <>
                    {/* Desktop navigation */}
                    <button 
                        className="hidden md:block absolute left-4 top-1/2 -translate-y-1/2 p-4 text-white hover:text-concrete-300 rounded-full transition-all z-[10001] cursor-pointer" 
                        onClick={(e) => { e.stopPropagation(); showPrev(e); }} 
                        aria-label="Vorheriges Bild"
                    >
                        <ArrowRight className="rotate-180" size={48} aria-hidden="true" />
                    </button>
                    <button 
                        className="hidden md:block absolute right-4 top-1/2 -translate-y-1/2 p-4 text-white hover:text-concrete-300 rounded-full transition-all z-[10001] cursor-pointer" 
                        onClick={(e) => { e.stopPropagation(); showNext(e); }} 
                        aria-label="Nächstes Bild"
                    >
                        <ArrowRight size={48} aria-hidden="true" />
                    </button>
                    
                    {/* Mobile navigation buttons (larger touch targets) */}
                    <button 
                        className="md:hidden absolute left-0 top-1/2 -translate-y-1/2 w-16 h-32 flex items-center justify-start pl-2 text-white/70 active:text-white active:bg-white/10 z-[10001]" 
                        onClick={(e) => { e.stopPropagation(); showPrev(e); }} 
                        aria-label="Vorheriges Bild"
                    >
                        <ArrowRight className="rotate-180" size={28} aria-hidden="true" />
                    </button>
                    <button 
                        className="md:hidden absolute right-0 top-1/2 -translate-y-1/2 w-16 h-32 flex items-center justify-end pr-2 text-white/70 active:text-white active:bg-white/10 z-[10001]" 
                        onClick={(e) => { e.stopPropagation(); showNext(e); }} 
                        aria-label="Nächstes Bild"
                    >
                        <ArrowRight size={28} aria-hidden="true" />
                    </button>
                </>
            )}
            
            <div className="relative p-2 md:p-12 flex items-center justify-center w-full h-full overflow-hidden">
                {/* Loading Spinner */}
                {isCurrentLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin w-10 h-10 border-4 border-white/30 border-t-white rounded-full"></div>
                    </div>
                )}
                <img 
                    key={currentIndex}
                    src={images[currentIndex]} 
                    className={`max-w-full max-h-[85vh] object-contain shadow-2xl rounded-sm select-none cursor-default transition-all duration-300 ease-out
                        ${isCurrentLoading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
                        ${slideDirection === 'left' ? 'animate-slide-out-left' : ''}
                        ${slideDirection === 'right' ? 'animate-slide-out-right' : ''}
                        ${!slideDirection && !isCurrentLoading ? 'animate-slide-in' : ''}
                    `} 
                    alt={`Gallery ${currentIndex}`} 
                    draggable="false"
                    onClick={(e) => e.stopPropagation()}
                />
                {images.length > 1 && (
                    <div className="absolute bottom-8 md:bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-1.5 rounded-full text-sm font-mono backdrop-blur-md border border-white/10">
                        {currentIndex + 1} / {images.length}
                    </div>
                )}
            </div>
            
            {/* Flow Animation Styles */}
            <style>{`
                @keyframes slide-out-left {
                    from { transform: translateX(0) scale(1); opacity: 1; }
                    to { transform: translateX(-100px) scale(0.9); opacity: 0; }
                }
                @keyframes slide-out-right {
                    from { transform: translateX(0) scale(1); opacity: 1; }
                    to { transform: translateX(100px) scale(0.9); opacity: 0; }
                }
                @keyframes slide-in {
                    from { transform: scale(0.95); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
                .animate-slide-out-left { animation: slide-out-left 0.25s ease-out forwards; }
                .animate-slide-out-right { animation: slide-out-right 0.25s ease-out forwards; }
                .animate-slide-in { animation: slide-in 0.3s ease-out forwards; }
            `}</style>
            
            {/* Thumbnail navigation on mobile */}
            {images.length > 1 && (
                <div className="md:hidden absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80vw] overflow-x-auto pb-2 px-4">
                    {images.map((img, idx) => (
                        <button
                            key={idx}
                            onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                            className={`w-12 h-12 shrink-0 rounded overflow-hidden border-2 transition-all ${
                                idx === currentIndex ? 'border-white scale-110' : 'border-transparent opacity-60'
                            }`}
                        >
                            <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </button>
                    ))}
                </div>
            )}
        </div>,
        document.body
    );
};

// Generic Section Component - MONOCHROME STYLES
export const SectionRenderer = ({ section }) => {
  const [lightboxState, setLightboxState] = useState({ isOpen: false, images: [], index: 0 });
  const [activeRoom, setActiveRoom] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOfficesInfoOpen, setIsOfficesInfoOpen] = useState(false);
  const { language, t } = useLanguage();
  const [nextEventImage, setNextEventImage] = useState(null);

  useEffect(() => {
    if (section.settings?.cta_type !== 'event_calendar') return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/events`);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const upcoming = response.data
          .filter(e => {
            const eventDate = new Date(e.date);
            const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
            return eventDay >= today;
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        if (!cancelled && upcoming.length > 0) {
          const next = upcoming[0];
          setNextEventImage(next.flyer_url || next.image_url || null);
        }
      } catch (error) {
        console.error("SectionRenderer: failed to fetch events for hero", error);
      }
    })();
    return () => { cancelled = true; };
  }, [section.settings?.cta_type]);

  const [formData, setFormData] = useState({
      firstName: '', lastName: '', company: '', email: '', startDate: '', employees: '', message: ''
  });

  const handleFormSubmit = (e) => {
      e.preventDefault();
      const subject = `Warteliste Anfrage: ${formData.company}`;
      const body = `Name: ${formData.firstName} ${formData.lastName}%0D%0A` +
                   `Firma: ${formData.company}%0D%0A` +
                   `Email: ${formData.email}%0D%0A` +
                   `Gewünschter Mietbeginn: ${formData.startDate}%0D%0A` +
                   `Anzahl Personen: ${formData.employees}%0D%0A%0D%0A` +
                   `Nachricht:%0D%0A${formData.message}`;
      window.location.href = `mailto:info@buerokult.de?subject=${subject}&body=${body}`;
  };

  if (!section) return null;

  const openLightbox = (images, index = 0) => {
      setLightboxState({ isOpen: true, images: Array.isArray(images) ? images : [images], index });
  };

  const closeLightbox = () => {
      setLightboxState(prev => ({ ...prev, isOpen: false }));
  };

  // Localization
  const title = language === 'en' && section.title_en ? section.title_en : section.title;
  const subtitle = language === 'en' && section.subtitle_en ? section.subtitle_en : section.subtitle;
  const content = language === 'en' && section.content_en ? section.content_en : section.content;
  const popupContent = language === 'en' && section.settings?.popup_content_en ? section.settings.popup_content_en : section.settings?.popup_content;
  let ctaText = section.settings?.cta_text || 'Mehr erfahren';
  if (language === 'en') ctaText = section.settings?.cta_text_en || 'More Info';

  const getLocalizedItem = (item) => ({
      ...item,
      title: language === 'en' && item.title_en ? item.title_en : item.title,
      description: language === 'en' && item.description_en ? item.description_en : item.description,
      features: language === 'en' && item.features_en ? item.features_en : item.features,
      popup_content: language === 'en' && item.popup_content_en ? item.popup_content_en : item.popup_content
  });

  switch (section.type) {
    case 'hero': return null; 
    
    case 'text_image':
      // Check if this is the first text_image section (building image)
      const isFirstSection = section.settings?.id === 'infos' || section.title?.includes('Arbeiten');
      
      return (
        <section id={section.settings?.id} className="py-12 md:py-24 px-4 md:px-12 bg-white overflow-hidden scroll-mt-20">
          <div className={`container mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-12 ${section.settings?.align === 'right' ? 'md:flex-row-reverse' : ''}`}>
             <div className={`w-full md:w-1/2 ${section.settings?.cta_type === 'event_calendar' ? 'md:max-w-[480px]' : ''} relative group`}>
                <div className="absolute top-4 left-4 w-full h-full border-2 border-concrete-300 translate-x-2 translate-y-2 group-hover:translate-x-4 group-hover:translate-y-4 transition-transform duration-300 hidden md:block"></div>
                
                {/* Special hover effect for building image */}
                {isFirstSection ? (
                  <div 
                    className={`relative w-full aspect-[4/3] overflow-hidden shadow-xl cursor-pointer ${section.settings?.cta_type === 'popup' ? 'active:scale-95 transition-transform' : ''}`}
                    onClick={() => {
                        if (section.settings?.cta_type === 'popup') {
                            setIsDialogOpen(true);
                        } else {
                            openLightbox([section.image_url]);
                        }
                    }}
                  >
                    {/* Base grayscale image */}
                    <img 
                      src={section.image_url || "/assets/twilight.jpg"} 
                      alt={title} 
                      className="w-full h-full object-cover grayscale"
                    />
                    {/* Color overlay - only visible on hover with mask for illuminated areas */}
                    <div 
                      className="absolute inset-0 w-full h-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                      style={{
                        maskImage: `
                          radial-gradient(ellipse 20% 28% at 73% 55%, black 0%, transparent 100%),
                          radial-gradient(ellipse 15% 10% at 70% 18%, black 0%, transparent 100%),
                          radial-gradient(circle 4% at 15% 90%, rgba(0,0,0,0.95) 0%, transparent 100%),
                          radial-gradient(circle 4% at 24% 87%, rgba(0,0,0,0.95) 0%, transparent 100%),
                          radial-gradient(circle 4% at 33% 84%, rgba(0,0,0,0.95) 0%, transparent 100%)
                        `,
                        WebkitMaskImage: `
                          radial-gradient(ellipse 20% 28% at 73% 55%, black 0%, transparent 100%),
                          radial-gradient(ellipse 15% 10% at 70% 18%, black 0%, transparent 100%),
                          radial-gradient(circle 4% at 15% 90%, rgba(0,0,0,0.95) 0%, transparent 100%),
                          radial-gradient(circle 4% at 24% 87%, rgba(0,0,0,0.95) 0%, transparent 100%),
                          radial-gradient(circle 4% at 33% 84%, rgba(0,0,0,0.95) 0%, transparent 100%)
                        `,
                        maskComposite: 'add',
                        WebkitMaskComposite: 'source-over'
                      }}
                    >
                      <img 
                        src={section.image_url || "/assets/twilight.jpg"} 
                        alt={title} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Subtle glow effect on hover */}
                    <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                      <div 
                        className="absolute"
                        style={{
                          top: '42%',
                          left: '62%',
                          width: '28%',
                          height: '40%',
                          background: 'radial-gradient(ellipse, rgba(255,180,80,0.12) 0%, transparent 70%)',
                          filter: 'blur(15px)'
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <img
                    src={(section.settings?.cta_type === 'event_calendar' ? nextEventImage : null) || section.image_url || "/assets/twilight.jpg"}
                    alt={title}
                    className={`relative w-full ${section.settings?.cta_type === 'event_calendar' ? 'aspect-[7/10]' : 'aspect-[4/3]'} object-cover grayscale group-hover:grayscale-0 transition-all duration-500 shadow-xl cursor-pointer ${section.settings?.cta_type === 'popup' ? 'active:scale-95 transition-transform' : ''}`}
                    onClick={() => {
                        if (section.settings?.cta_type === 'popup') {
                            setIsDialogOpen(true);
                        } else {
                            openLightbox([(section.settings?.cta_type === 'event_calendar' ? nextEventImage : null) || section.image_url]);
                        }
                    }}
                  />
                )}
             </div>
             <div className={`w-full md:w-1/2 space-y-6 flex flex-col ${section.settings?.text_align === 'center' ? 'items-center text-center' : ''}`}>
                <h2 className="text-2xl md:text-5xl font-heading font-bold text-black leading-tight">{title}</h2>
                <div className="w-20 h-1 bg-black"></div>
                <div className="text-base md:text-lg text-concrete-600 leading-relaxed font-sans prose prose-neutral" dangerouslySetInnerHTML={{ __html: content }} />
                
                {section.settings?.cta_text && (
                    <>
                    {section.settings.cta_type === 'waiting_list' ? (
                        /* OFFICES SECTION: Two Buttons - Warteliste (black) + Weitere Infos (white) */
                        <div className="flex flex-col sm:flex-row gap-3 mt-4">
                            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button 
                                        className="bg-black text-white hover:bg-concrete-800 rounded-none uppercase tracking-widest flex-1"
                                    >
                                        {ctaText} 
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto bg-white border-none p-0 rounded-none">
                                    <div className="p-6 md:p-8">
                                        <DialogHeader className="mb-4">
                                            <DialogTitle className="text-2xl font-bold font-heading text-center text-black">
                                                {language === 'en' ? 'Join Waiting List' : 'In Warteliste eintragen'}
                                            </DialogTitle>
                                        </DialogHeader>
                                        <form onSubmit={handleFormSubmit} className="space-y-4">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div><label className="block text-sm font-medium mb-1 text-concrete-700">{language === 'en' ? 'First Name' : 'Vorname'}</label><input required type="text" className="w-full p-2 border border-concrete-300 outline-none focus:border-black" onChange={e => setFormData({...formData, firstName: e.target.value})} /></div>
                                                <div><label className="block text-sm font-medium mb-1 text-concrete-700">{language === 'en' ? 'Last Name' : 'Name'}</label><input required type="text" className="w-full p-2 border border-concrete-300 outline-none focus:border-black" onChange={e => setFormData({...formData, lastName: e.target.value})} /></div>
                                            </div>
                                            <div><label className="block text-sm font-medium mb-1 text-concrete-700">{language === 'en' ? 'Company' : 'Firmenname'}</label><input required type="text" className="w-full p-2 border border-concrete-300 outline-none focus:border-black" onChange={e => setFormData({...formData, company: e.target.value})} /></div>
                                            <div><label className="block text-sm font-medium mb-1 text-concrete-700">{language === 'en' ? 'Email' : 'Email-Adresse'}</label><input required type="email" className="w-full p-2 border border-concrete-300 outline-none focus:border-black" onChange={e => setFormData({...formData, email: e.target.value})} /></div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div><label className="block text-sm font-medium mb-1 text-concrete-700">{language === 'en' ? 'Start Date' : 'Mietbeginn'}</label><input required type="date" className="w-full p-2 border border-concrete-300 outline-none focus:border-black" onChange={e => setFormData({...formData, startDate: e.target.value})} /></div>
                                                <div><label className="block text-sm font-medium mb-1 text-concrete-700">{language === 'en' ? 'Employees' : 'Anzahl Personen'}</label><input required type="number" className="w-full p-2 border border-concrete-300 outline-none focus:border-black" onChange={e => setFormData({...formData, employees: e.target.value})} /></div>
                                            </div>
                                            <div><label className="block text-sm font-medium mb-1 text-concrete-700">{language === 'en' ? 'Message' : 'Nachricht an teamWERK'}</label><textarea className="w-full p-2 border border-concrete-300 outline-none h-24 focus:border-black" onChange={e => setFormData({...formData, message: e.target.value})} /></div>
                                            <Button type="submit" className="w-full bg-black text-white mt-4 hover:bg-concrete-800">{language === 'en' ? 'Send' : 'Absenden'}</Button>
                                            <p className="text-xs text-concrete-600 text-center">{language === 'en' ? <>We use your details only to answer your request. See our <a href="/datenschutz" className="underline">privacy policy</a>.</> : <>Deine Angaben nutzen wir nur zur Bearbeitung deiner Anfrage. Details in der <a href="/datenschutz" className="underline">Datenschutzerklärung</a>.</>}</p>
                                        </form>
                                    </div>
                                </DialogContent>
                            </Dialog>
                            
                            {/* Weitere Infos Button - White */}
                            <Dialog open={isOfficesInfoOpen} onOpenChange={setIsOfficesInfoOpen}>
                                <DialogTrigger asChild>
                                    <Button 
                                        variant="outline"
                                        className="bg-white text-black border-2 border-black hover:bg-concrete-100 rounded-none uppercase tracking-widest flex-1"
                                    >
                                        {language === 'en' ? 'More Info' : 'Weitere Infos'}
                                        <Info className="ml-2 h-4 w-4" />
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto bg-white border-none p-0 rounded-none">
                                    <div className="p-6 md:p-8">
                                        <DialogHeader className="mb-6 border-b border-concrete-200 pb-4">
                                            <DialogTitle className="text-2xl md:text-3xl font-bold font-heading text-center text-black">
                                                {language === 'en' ? 'Office Services' : 'Leistungsumfang Büro'}
                                            </DialogTitle>
                                        </DialogHeader>
                                        
                                        <div className="grid md:grid-cols-2 gap-8">
                                            {/* Left Column - Main Services */}
                                            <div className="space-y-6">
                                                <div>
                                                    <h3 className="text-xl font-bold font-heading mb-4 text-black border-b border-concrete-200 pb-2">
                                                        {language === 'en' ? 'Scope of Services' : 'Leistungsumfang'}
                                                    </h3>
                                                    <ul className="space-y-2 text-concrete-700">
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Eigener Büroraum</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Nutzung Work-Café inkl. Inventar (auch für Besucher)</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Kaffee, Espresso & Cappuccino inklusive</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Gekühltes und gefiltertes Mineralwasser inklusive</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Gebäudereinigung (WC 3x Wöchentlich)</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Reinigung des eigenen Büros (wöchentlich)</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Müllentsorgung</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Sanitäre Anlagen inkl. Verbrauchsmaterial</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Parkplatz am Haus *nicht garantiert</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Jährliche Wartungen (Aufzug, TÜV) etc.</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-5 w-5 text-black flex-shrink-0 mt-0.5" /><span>Möblierte Außenterrasse im westlichen Bereich EG</span></li>
                                                    </ul>
                                                </div>
                                            </div>
                                            
                                            {/* Right Column - Additional Services & Benefits */}
                                            <div className="space-y-6">
                                                <div>
                                                    <h3 className="text-xl font-bold font-heading mb-4 text-black border-b border-concrete-200 pb-2">
                                                        {language === 'en' ? 'Additional Services' : '...weitere Leistungen'}
                                                    </h3>
                                                    <ul className="space-y-2 text-concrete-700 text-sm">
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Für Festnetztelefon wird Cloud-Telefonie-Dienst vorausgesetzt</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Aktuell 200 Mbit verfügbar *Glasfaserausbau läuft</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Grundbeleuchtung schaltbar an der Türe</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Multifunktions-Kopiergerät mit Follow Me Funktion, unbegrenzt drucken</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Büros mit elektrischen Jalousien</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Hochwertige Trockenbauwände für erhöhten Schallschutz</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Vollspan-Türen mit Absenkdichtung</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>RFID Chip für Zugangskontrolle</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Unabhängig der Mitarbeiterzahl</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>All Inkl.</span></li>
                                                    </ul>
                                                </div>
                                                
                                                <div>
                                                    <h3 className="text-xl font-bold font-heading mb-4 text-black border-b border-concrete-200 pb-2">
                                                        {language === 'en' ? 'Exclusive Benefits' : 'Exklusive Vorteile'}
                                                    </h3>
                                                    <ul className="space-y-2 text-concrete-700 text-sm">
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Eigener Briefkasten</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Logo und Beschriftung im gesamten Wege-Leitsystem sowie neben dem Haupteingang</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Nennung mit Logo auf der Website www.teamwerk-erolzheim.de</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Nutzung der Technik in den Besprechungsräumen</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Pedros Café und Bar</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>2x Hopfen & Herz Talk-Event jährlich</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>2x Wein & Wahrheit Talk-Event jährlich</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>1x Jährlich TeamWERK Sommerfest</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>2x Kulturelle Veranstaltung (Konzert o.Ä.)</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Eine freundliche und kreative Umgebung</span></li>
                                                        <li className="flex items-start gap-2"><Check className="h-4 w-4 text-black flex-shrink-0 mt-0.5" /><span>Design-Office</span></li>
                                                    </ul>
                                                </div>
                                                
                                                {/* Note about electricity */}
                                                <div className="bg-concrete-100 p-4 rounded-sm text-sm text-concrete-600">
                                                    <p className="font-medium text-black mb-1">*Ausgenommen der Verbrauch von Strom im Büro ist unverhältnismäßig (Starkstrom-Geräte, Kühlanlagen, Messtechnik o.Ä.)</p>
                                                    <p>Bitte geben Sie die Bedarfe über den normalen Bürobetrieb hinausgehende Verbräuche vorher an.</p>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Gallery Section */}
                                        {section.settings?.popup_gallery_images && section.settings.popup_gallery_images.length > 0 && (
                                            <div className="mt-8 pt-6 border-t border-concrete-200">
                                                <h3 className="text-xl font-bold font-heading mb-4 text-black">{t('gallery')}</h3>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                    {section.settings.popup_gallery_images.map((img, i) => (
                                                        <div 
                                                            key={i} 
                                                            className="relative aspect-square overflow-hidden group cursor-pointer active:scale-95 transition-transform" 
                                                            onClick={() => {
                                                                const allImages = [section.image_url, ...(section.settings.popup_gallery_images || [])];
                                                                openLightbox(allImages, i + 1);
                                                            }}
                                                        >
                                                            <img src={img} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" alt={`Gallery ${i}`} loading="lazy" />
                                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                                                <span className="text-white opacity-0 group-hover:opacity-100 font-bold text-2xl">+</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>
                    ) : (
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                             <Button 
                                className="mt-4 bg-black text-white hover:bg-concrete-800 rounded-none uppercase tracking-widest w-full md:w-auto"
                                onClick={(e) => {
                                    if (section.settings.cta_type === 'event_calendar') {
                                        setIsDialogOpen(true);
                                    } else if (section.settings.cta_type === 'popup') {
                                        setIsDialogOpen(true);
                                    } else if (section.settings?.cta_link) {
                                        window.open(section.settings.cta_link, '_blank');
                                        e.preventDefault(); 
                                    }
                                }}
                            >
                                {ctaText} 
                                {section.settings.cta_type === 'event_calendar' ? <CalendarIcon className="ml-2 h-4 w-4" /> : <ArrowRight className="ml-2 h-4 w-4" />}
                            </Button>
                        </DialogTrigger>
                        
                        <DialogContent
                            className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto bg-white border-none p-0 rounded-none"
                            onInteractOutside={(e) => { const t = e.detail?.originalEvent?.target; if (t?.closest?.('[data-lightbox]')) e.preventDefault(); }}
                            onEscapeKeyDown={(e) => { if (document.querySelector('[data-lightbox]')) e.preventDefault(); }}
                        >
                            {section.settings.cta_type === 'event_calendar' ? (
                                <div className="p-4 md:p-8">
                                    <DialogHeader className="mb-4 md:mb-6 border-b border-concrete-200 pb-4">
                                        <DialogTitle className="text-2xl md:text-3xl font-bold font-heading text-center text-black">
                                            {language === 'en' ? 'Event Calendar' : 'Eventkalender'}
                                        </DialogTitle>
                                    </DialogHeader>
                                    <EventList />
                                </div>
                            ) : (
                                <div className="p-4 md:p-8">
                                    <DialogHeader className="mb-4 md:mb-6 border-b border-concrete-200 pb-4">
                                        <div className="flex flex-col items-center justify-center space-y-4">
                                            <DialogTitle className="text-2xl md:text-3xl font-bold font-heading text-center text-black">{title}</DialogTitle>
                                        </div>
                                    </DialogHeader>
                                    
                                    <div className={section.settings.popup_gallery_images && section.settings.popup_gallery_images.length > 0 ? "grid md:grid-cols-2 gap-8" : "max-w-2xl mx-auto"}>
                                        <div className="space-y-6">
                                            <div 
                                                className="cursor-pointer overflow-hidden rounded-sm shadow-md relative group"
                                                onClick={() => {
                                                    const allImages = [section.image_url, ...(section.settings.popup_gallery_images || [])];
                                                    openLightbox(allImages, 0);
                                                }}
                                            >
                                                <img 
                                                    src={section.image_url} 
                                                    className="w-full h-48 md:h-64 object-cover transform group-hover:scale-105 transition-transform duration-300" 
                                                    alt={title}
                                                />
                                            </div>
                                            <div className="prose prose-neutral max-w-none text-sm" dangerouslySetInnerHTML={{ __html: popupContent }} />
                                        </div>
                                        
                                        {section.settings.popup_gallery_images && section.settings.popup_gallery_images.length > 0 && (
                                            <div>
                                                <h3 className="text-xl font-bold font-heading mb-4 text-black border-b border-concrete-200 pb-2">{t('gallery')}</h3>
                                                <div className="grid grid-cols-2 gap-2 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                                                    {section.settings.popup_gallery_images.map((img, i) => (
                                                        <div key={i} className="relative aspect-square overflow-hidden group cursor-pointer active:scale-95 transition-transform" onClick={() => {
                                                            const allImages = [section.image_url, ...(section.settings.popup_gallery_images || [])];
                                                            openLightbox(allImages, i + 1);
                                                        }}>
                                                            <img src={img} className="w-full h-full object-cover" alt={`Gallery ${i}`} loading="lazy" />
                                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                                                <span className="text-white opacity-0 group-hover:opacity-100 font-bold text-2xl">+</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </DialogContent>
                    </Dialog>
                    )}
                    </>
                )}
             </div>
          </div>
          {lightboxState.isOpen && <Lightbox images={lightboxState.images} initialIndex={lightboxState.index} onClose={closeLightbox} />}
        </section>
      );

    case 'rooms_grid':
        return (
            <section id="meeting-rooms" className="py-12 md:py-24 bg-concrete-50 px-4 scroll-mt-20">
                <div className="container mx-auto">
                    <div className="text-center mb-8 md:mb-16 max-w-2xl mx-auto">
                        <h2 className="text-2xl md:text-5xl font-heading font-bold mb-4 text-black">{title}</h2>
                        <p className="text-concrete-500 text-lg">{subtitle}</p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {section.items?.map(getLocalizedItem).map((item, idx) => (
                            <div key={idx} className="bg-white group hover:shadow-2xl transition-all duration-500 border border-concrete-200 overflow-hidden flex flex-col h-full">
                                <div className="relative h-64 overflow-hidden flex-shrink-0 cursor-pointer active:opacity-90" onClick={() => setActiveRoom(item)}>
                                    <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors z-10"></div>
                                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"/>
                                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 text-xs font-bold uppercase tracking-wider z-20 text-black">{item.capacity}</div>
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-30">
                                        <div className="bg-white/90 rounded-full p-3 shadow-lg">
                                            <Info size={24} className="text-black" />
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6 md:p-8 flex flex-col flex-1">
                                    <h3 className="text-2xl font-bold mb-2 font-heading group-hover:text-black transition-colors">{item.title}</h3>
                                    <p className="text-concrete-500 mb-6">{item.description}</p>
                                    <div className="flex flex-wrap gap-4 mb-8 flex-1">
                                        {item.features?.map((feat, f_idx) => (
                                            <div key={f_idx} className="flex items-center text-sm text-concrete-600">
                                                <Check size={14} className="text-black mr-2" /> {feat}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex flex-col md:flex-row gap-2 mt-auto">
                                        <Button className="flex-1 border-2 border-black bg-black text-white hover:bg-white hover:text-black rounded-none uppercase tracking-widest font-bold"
                                            onClick={() => {
                                                const title = item.title?.toLowerCase() || '';
                                                // Navigate to anny.co booking widget for Seeblick/Waldblick
                                                if (title.includes('seeblick')) {
                                                    window.location.href = '/raum-buchen/seeblick';
                                                } else if (title.includes('waldblick')) {
                                                    window.location.href = '/raum-buchen/waldblick';
                                                } else if (item.cta_link) {
                                                    if (item.cta_link.startsWith('/')) {
                                                        window.location.href = item.cta_link;
                                                    } else {
                                                        window.open(item.cta_link, '_blank');
                                                    }
                                                }
                                            }}>
                                            {t('book')}
                                        </Button>
                                        {item.popup_content && (
                                            <Button variant="outline" className="flex-1 border-2 border-black text-black hover:bg-concrete-100 rounded-none uppercase tracking-widest font-bold" onClick={() => setActiveRoom(item)}>
                                                {t('more_info')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                <Dialog open={!!activeRoom} onOpenChange={(open) => !open && setActiveRoom(null)}>
                    <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto bg-white border-none p-0 rounded-none">
                        {activeRoom && (
                            <div className="p-4 md:p-8">
                                <DialogHeader className="mb-4 md:mb-6 border-b border-concrete-200 pb-4">
                                    <DialogTitle className="text-2xl md:text-3xl font-bold font-heading text-center text-black">{activeRoom.title}</DialogTitle>
                                </DialogHeader>
                                <div className={activeRoom.popup_gallery_images && activeRoom.popup_gallery_images.length > 0 ? "grid md:grid-cols-2 gap-8" : "max-w-2xl mx-auto"}>
                                    <div className="space-y-6">
                                        <div 
                                            className="cursor-pointer overflow-hidden rounded-sm shadow-md relative group"
                                            onClick={() => {
                                                const allImages = [activeRoom.image_url, ...(activeRoom.popup_gallery_images || [])];
                                                openLightbox(allImages, 0);
                                            }}
                                        >
                                            <img 
                                                src={activeRoom.image_url} 
                                                className="w-full h-48 md:h-64 object-cover transform group-hover:scale-105 transition-transform duration-300" 
                                                alt={activeRoom.title}
                                            />
                                        </div>
                                        <div className="prose prose-neutral max-w-none text-sm" dangerouslySetInnerHTML={{ __html: activeRoom.popup_content }} />
                                    </div>
                                    {activeRoom.popup_gallery_images && activeRoom.popup_gallery_images.length > 0 && (
                                        <div>
                                            <h3 className="text-xl font-bold font-heading mb-4 text-black border-b border-concrete-200 pb-2">{t('gallery')}</h3>
                                            <div className="grid grid-cols-2 gap-2 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                                                {activeRoom.popup_gallery_images.map((img, i) => (
                                                    <div key={i} className="relative aspect-square overflow-hidden group cursor-pointer active:scale-95 transition-transform" onClick={() => {
                                                        const allImages = [activeRoom.image_url, ...(activeRoom.popup_gallery_images || [])];
                                                        openLightbox(allImages, i + 1);
                                                    }}>
                                                        <img src={img} className="w-full h-full object-cover" alt={`Gallery ${i}`} loading="lazy" />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
                {lightboxState.isOpen && <Lightbox images={lightboxState.images} initialIndex={lightboxState.index} onClose={closeLightbox} />}
            </section>
        );

    case 'features':
       return (
         <section id="community" className="py-12 md:py-24 bg-white px-4 scroll-mt-20">
            <div className="container mx-auto">
                <div className="text-center mb-12 md:mb-16 max-w-2xl mx-auto">
                    <h2 className="text-2xl md:text-4xl font-heading font-bold mb-4 text-black">{title}</h2>
                    <p className="text-concrete-500">{subtitle}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {section.items?.map(getLocalizedItem).map((item, idx) => {
                        const Icon = ICON_MAP[item.icon] || Check;
                        return (
                            <div key={idx} className="bg-concrete-50 p-8 border border-transparent hover:border-black transition-colors duration-300 group hover:bg-white hover:shadow-lg">
                                <div className="w-12 h-12 bg-white rounded-none flex items-center justify-center mb-6 text-black shadow-sm group-hover:scale-110 group-hover:bg-black group-hover:text-white transition-all duration-300">
                                    <Icon size={24} />
                                </div>
                                <h3 className="text-lg md:text-xl font-bold mb-3 font-heading uppercase tracking-wide text-black">{item.title}</h3>
                                <p className="text-concrete-500 leading-relaxed text-sm">{item.description}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
         </section>
       );

    case 'tenants_grid':
        return (
            <section id={section.settings?.id || 'tenants'} className="py-12 md:py-24 bg-concrete-50 px-4 scroll-mt-20">
                <div className="container mx-auto">
                    <div className="text-center mb-12 md:mb-16 max-w-2xl mx-auto">
                        <h2 className="text-2xl md:text-4xl font-heading font-bold mb-4 text-black">{title}</h2>
                        <p className="text-concrete-500">{subtitle}</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {section.items?.map(getLocalizedItem).map((tenant, idx) => (
                            <a 
                                key={idx} 
                                href={tenant.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="bg-white p-6 border border-concrete-200 hover:border-black transition-all duration-300 group hover:shadow-lg flex flex-col"
                            >
                                <div className="h-16 flex items-center justify-center mb-4">
                                    {tenant.logo_url ? (
                                        <img 
                                            src={tenant.logo_url} 
                                            alt={tenant.name} 
                                            className="max-h-full max-w-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 bg-concrete-100 rounded-full flex items-center justify-center text-concrete-400 group-hover:bg-black group-hover:text-white transition-all">
                                            <Building2 size={24} />
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-base font-bold mb-2 font-heading text-black group-hover:text-black">{tenant.name}</h3>
                                <p className="text-concrete-500 text-sm flex-1">{tenant.description}</p>
                                <div className="mt-4 pt-4 border-t border-concrete-100 flex items-center justify-between text-xs text-concrete-400 group-hover:text-black transition-colors">
                                    <span className="truncate">{tenant.website?.replace('https://', '').replace('http://', '')}</span>
                                    <ExternalLink size={14} className="shrink-0 ml-2" />
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>
        );

    default: return null;
  }
};
